/*
 * Copyright (c) Worldline 2023.
 */

import {Component, OnInit} from '@angular/core';
import {SharedService} from "../../services/shared.service";
import {NgxSpinnerService} from "ngx-spinner";
import {HttpService} from "../../http/http.service";
import {forkJoin, Observable} from 'rxjs';
import {SignatureDto} from "../../model/signatureDto";
import {Response} from "../../model/response";
import {ActivatedRoute, Router} from "@angular/router";
import {DocumentDto} from "../../model/documentDto";
import {RootScopeService} from "../../services/rootscope.service";
import {RedirectionService} from "../../services/redirection.service";
import {Steps} from "../../constant/steps";
import {SessionService} from "../../services/session.service";
import {SignerDto} from "../../model/signerDto";
import SignatureAction = SignatureDto.ActionEnum;
import ExternalDocumentStatusEnum = DocumentDto.ExternalDocumentStatusEnum;
import ExternalSignerStatusEnum = SignerDto.ExternalSignerStatusEnum;

@Component({
    selector: 'app-resume',
    providers: [{
        provide: '$routeParams',
        useFactory: ($injector: any) => $injector.get('$routeParams'),
        deps: ['$routeParams']
    }],
    templateUrl: './resume.component.html'
})
export class ResumeComponent implements OnInit {

    private sessionId;
    downloadOnSummaryPage;
    private currentSigner;
    allSigned;
    urlOk;
    private urlOnHold;
    private mode;
    documentToView;
    documents;
    hasTrueSignature;
    private errorMsg;
    isSmartPhoneType;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private shared: SharedService,
                private http: HttpService,
                private spinnerService: NgxSpinnerService,
                private rootScope: RootScopeService,
                public redirectionService: RedirectionService,
                public sessionService: SessionService) {
        this.documents = []
    }

    ngOnInit(): void {
        this.rootScope.get().step = Steps.resume;
        this.isSmartPhoneType = this.rootScope.get().isSmartphoneType;
        // Check the sessionId
        this.sessionId = this.route.snapshot.params['sessionId'];

        if (this.sessionId !== this.sessionService.id) {
            console.warn('The session ID in the url is not the same as the saved : url->' + this.sessionId + ' | saved->' + this.sessionService.id);
            this.redirectionService.session(this.route, this.sessionId);
            return;
        }

        // Check the status of the session
        if (typeof this.sessionService.info === 'undefined') {//|| this.sessionService.info.externalSessionStatus !== this.externalSessionStatus.init) {
            console.warn('The current signer is undefined or is not at the init status');
            this.redirectionService.session(this.route, this.sessionId);
            return;
        }

        //Check if template allow document download
        this.downloadOnSummaryPage = this.rootScope.get().properties.downloadOnSummaryPage;

        // Enable spin
        this.spinnerService.show('spinner');

        this.allSigned = true;

        this.getDocuments();

        this.urlOk = this.sessionService.getUrlOk();

        this.urlOnHold = this.sessionService.getUrlOnHold();
    }

    mail() {
        return this.sessionService.info.configDto.sendResume && this.currentSigner?.email;
    }

    getDocuments() {
        let that = this;
        console.debug('Getting document list.');
        this.http.getAllSigners(this.sessionId).toPromise().then(function (response) {
            // Disable spin
            that.spinnerService.hide('spinner');
            if (response?.success) {

                let signers = response.result;
                let observables = Array<Observable<Response>>();

                that.hasTrueSignature = false;

                //closure to bind signer to promise
                (signers as any).forEach(signer => {
                    let observable = that.http.getAllSignatures(that.sessionId, signer.extSignerId);
                    observables.push(observable);
                })

                forkJoin(observables).subscribe({
                    next: responses => that.handleSignaturesResponse(responses),
                    error(error) {
                        console.warn(error);
                        that.errorMsg = 'error.unreachable';
                        that.spinnerService.hide('spinner');
                    }
                })

            } else {
                console.warn('An error occured when getting the documents list');
                console.warn(response?.error?.msg);
                that.errorMsg = 'error.document.download';
            }
        }, function (error) {
            // If communication error
            console.warn(error);
            that.errorMsg = 'error.unreachable';
            that.spinnerService.hide('spinner');
        });
    }

    handleSignaturesResponse(responses){
        responses.forEach(resp => {
            let signatures = resp.result as Array<SignatureDto>;
            let signer = signatures[0].signerDto!!;

            console.debug('signer: ' + signer.lastName);
            if (signer.externalSignerStatus === ExternalSignerStatusEnum.Completed) {
                this.currentSigner = signer;
            }

            signatures.forEach(signature => {
                console.debug('signature:' + signature.documentDto!.extDocumentId);

                let document = this.documents.find(doc => doc.extDocumentId === signature.documentDto!.extDocumentId)
                if (document) {
                    switch (signature.action) {

                        case SignatureAction.Approval:
                            document.validators.push(signer);
                            break;

                        case SignatureAction.Signature:
                        default:
                            if (signer.signerType === 'CUSTOMER') {
                                document.customerSigners.push(signer);
                            } else {
                                document.prospectSigners.push(signer);
                            }
                            this.hasTrueSignature = true;
                            break;
                    }
                } else {
                    document = signature.documentDto;
                    document.customerSigners = [];
                    document.prospectSigners = [];
                    document.validators = [];

                    switch (signature.action) {

                        case SignatureAction.Approval:
                            document.validators.push(signer);
                            break;

                        case SignatureAction.Signature:
                        default:
                            if (signer.signerType === 'CUSTOMER') {
                                document.customerSigners.push(signer);
                            } else {
                                document.prospectSigners.push(signer);
                            }
                            this.hasTrueSignature = true;
                            break;
                    }
                    this.documents.push(document);
                }
            })

        })

        this.documents.forEach(doc => {
            let properties = this.rootScope.get().properties;
            let url = properties.api.urlraw + '/sessions/:sessionId/documents/:documentId/data/:format';
            url = url.replace(':sessionId', this.sessionId);
            url = url.replace(':documentId', doc.extDocumentId);
            doc.urlDownload = url.replace(':format', 'raw');
            doc.urlView = url.replace(':format', 'view');

            doc.urlRaw = properties.api.urlraw;
            doc.url = properties.api.url;
            doc.sessionId = this.sessionId;
            doc.documentId = doc.extDocumentId;

            if (doc.externalDocumentStatus === ExternalDocumentStatusEnum.SignaturesRemaining) {
                this.allSigned = false;
            }
        })
    }

    displayRedirect() {
        this.urlOk = this.sessionService.getUrlOk();
        return this.urlOk !== undefined && this.urlOk !== null;
    }

    setDocumentToView(document) {
        console.debug('function setDocumentToView called ! sessionId : ' + document.sessionId + ' documentId : ' + document.documentId);

        //pdfReader type
        this.mode = this.sessionService.info.configDto.pdfReaderType;

        if ((this.documentToView !== undefined) && (this.documentToView !== document)) {
            this.documentToView = document;
            this.shared.broadcast('documentUpdate', this.documentToView);
        } else {
            this.documentToView = document;
        }
    }

    downloadPdf(doc) {
        let fileName = doc.documentName + '.pdf';
        let a = document.createElement('a');
        document.body.appendChild(a);
        this.http.httpClient.get(doc.urlDownload, {
            headers: this.http.getHttpOptions()['headers'],
            responseType: 'blob'
        }).subscribe(function (response) {
            let file = new Blob([response], {type: 'application/pdf'});
            a.href = window.URL.createObjectURL(file);
            a.download = fileName;
            a.click();
        }, function (error) {
            console.error('Unable to download document', error);
        });
    }

    getPdfInfoUrl() {
        return this.rootScope.get().properties.api.urlraw + '/sessions/' + this.sessionId + '/pdfs/' + this.documentToView.extDocumentId + '/1'
    }

    getToken() {
        return sessionStorage.getItem("token") as string;
    }

}
